import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import MarketsLayout from '@fuww/library/src/Markets';
import Head from '../Head';
import MarketPlaceHeading from '../Markets/Heading';
import Tabs from '../Tabs';
import StandTabs from '../Stand/Tabs';
import withQuery from '../../lib/withQuery';
import getProductsBreadcrumbs from './getBreadcrumbs';
import standQuery from '../../lib/queries/products/standQuery.graphql';
import marketQuery from '../../lib/queries/products/marketQuery.graphql';
import messages from '../../lib/messages.json';
import throw404 from '../../lib/throw404';

const standHeadingPropertyTypes = {
  data: PropTypes.shape({
    stand: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      market: PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
      }).isRequired,
    }),
  }).isRequired,
  marketId: PropTypes.number,
};

type StandHeadingProperties = InferProps<typeof standHeadingPropertyTypes>;

const StandHeading = withQuery(({
  data: { stand },
  marketId: marketIdParameter,
}: StandHeadingProperties) => {
  const intl = useIntl();

  if (!stand) {
    return throw404();
  }

  const {
    id: standId, name, description, market: {
      id: marketId,
      name: marketName,
    },
  } = stand;

  if (marketIdParameter !== marketId) {
    return throw404();
  }

  return (
    <>
      <Head
        title={
          intl.formatMessage(messages['products.stand.title'], {
            name, marketName,
          })
        }
        description={description}
        subjectType="Stand"
        subjectId={standId}
        payload={{ marketId }}
      />
      <MarketsLayout
        title={
          intl.formatMessage(messages['products.stand.headingTitle'], {
            name, marketName,
          })
        }
        description={description}
        breadcrumbs={getProductsBreadcrumbs({
          marketName, marketId, standName: name, standId,
        })}
        tabs={<StandTabs />}
      />
    </>
  );
});

const marketHeadingPropertyTypes = {
  data: PropTypes.shape({
    market: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

type MarketHeadingProperties = InferProps<typeof marketHeadingPropertyTypes>;

const MarketHeading = withQuery(({
  data: {
    market,
  },
}: MarketHeadingProperties) => {
  const intl = useIntl();

  if (!market) {
    return throw404();
  }

  const {
    id: marketId,
    name,
    description,
  } = market;

  return (
    <>
      <Head
        title={intl.formatMessage(messages['products.market.title'], { name })}
        description={
          description
          || intl.formatMessage(messages['markets.description'])
        }
        subjectType="Market"
        subjectId={marketId}
      />
      <MarketsLayout
        title={
          intl.formatMessage(messages['products.market.headingTitle'], { name })
        }
        description={description}
        breadcrumbs={getProductsBreadcrumbs({
          marketName: name, marketId,
        })}
        tabs={<Tabs />}
      />
    </>
  );
});

const headingPropertyTypes = {
  marketId: PropTypes.number,
  standId: PropTypes.number,
};

type HeadingProperties = InferProps<typeof headingPropertyTypes>;

const Heading = ({
  marketId: marketIdProperty,
  standId: standIdProperty,
}: HeadingProperties) => {
  const marketId = marketIdProperty ?? null;
  const standId = standIdProperty ?? null;

  if (standId) {
    return (
      <StandHeading
        query={standQuery}
        variables={{ standId }}
        marketId={marketId}
        errorMessage="Error loading stand information"
      />
    );
  }

  if (marketId) {
    return (
      <MarketHeading
        query={marketQuery}
        variables={{ id: marketId }}
        errorMessage="Error loading market information"
      />
    );
  }

  return (
    <MarketPlaceHeading
      breadcrumbs={getProductsBreadcrumbs({})}
    />
  );
};

Heading.propTypes = headingPropertyTypes;

export default Heading;
